import { getIsThemePreview, getPreviewJwt } from './metadata';

/**
 * - `live`: a real contact using the portal. Real data, writes allowed.
 * - `masquerade`: an admin viewing the portal as a specific contact. Real data
 *   scoped to that contact, read-only.
 * - `preview`: an admin previewing with no contact. Mock or sample data,
 *   read-only.
 */
export type PortalSessionMode = 'live' | 'masquerade' | 'preview';

const MASQUERADE_CONTACT_VID_CLAIM = 'masquerade_contact_vid';

// Decoded without signature verification: only used to pick UI behavior. The
// backend verifies the token and enforces access.
const decodeJwtPayload = (jwt: string): Record<string, unknown> | undefined => {
  const payload = jwt.split('.')[1];
  if (!payload) {
    return undefined;
  }
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );
    const decoded: unknown = JSON.parse(atob(padded));
    return decoded !== null && typeof decoded === 'object'
      ? (decoded as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
};

export const getMasqueradeContactVidFromJwt = (
  previewJwt: string | undefined
): number | undefined => {
  if (!previewJwt) {
    return undefined;
  }
  const vid = decodeJwtPayload(previewJwt)?.[MASQUERADE_CONTACT_VID_CLAIM];
  return typeof vid === 'number' ? vid : undefined;
};

export const getPortalSessionModeFrom = ({
  previewJwt,
  isThemePreview,
}: {
  previewJwt: string | undefined;
  isThemePreview: boolean;
}): PortalSessionMode => {
  if (getMasqueradeContactVidFromJwt(previewJwt) !== undefined) {
    return 'masquerade';
  }
  if (previewJwt || isThemePreview) {
    return 'preview';
  }
  return 'live';
};

export function getPortalSessionMode(): PortalSessionMode {
  return getPortalSessionModeFrom({
    previewJwt: getPreviewJwt(),
    isThemePreview: getIsThemePreview(),
  });
}
